import ChatGPTService from '@src/services/ChatGPTService';
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import DeleteIcon from '../../svgs/DeleteIcon';
import PencilIcon from '../../svgs/PencilIcon';
import Accordion from './Accordion';

import type { BrandingGoal } from '@src/types.ts/story';

// Styled Components
const GoalsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GoalItemContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px;
  border: 1px solid #484848;
  border-radius: 8px;
`;

const GoalContent = styled.div`
  flex: 1;
`;

const GoalLabel = styled.h4`
  color: #f3e9d7;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const GoalDescription = styled.p`
  color: #848484;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`;

const GoalActions = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s;

  ${GoalItemContainer}:hover & {
    opacity: 1;
  }
`;

const IconButton = styled.button<{ isEdit?: boolean; isDelete?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  opacity: ${props => (props.disabled ? 0.5 : 1)};

  &:hover:not(:disabled) {
    svg path {
      transition: all 0.2s;
      stroke: ${props =>
        props.isEdit ? '#F2D093' : props.isDelete ? '#ff4545' : '#848484'};
    }
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const AddButton = styled.button<{ isSaving?: boolean }>`
  align-self: flex-end;
  display: block;
  height: 48px;
  padding: 12px 24px;
  border-radius: 8px;
  background: #17c964;
  color: #03041a;
  font-size: 14px;
  font-weight: 700;
  border: none;
  transition: 0.2s;
  opacity: ${({ isSaving }) => (isSaving ? 0.6 : 1)};
  cursor: ${({ isSaving }) => (isSaving ? 'not-allowed' : 'pointer')};
  &:hover:not(:disabled) {
    filter: brightness(1.2) saturate(1.2);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px;
  border: 1px solid #484848;
  border-radius: 8px;
`;

const FormInput = styled.input`
  border: none;
  background: transparent;
  color: #f3e9d7;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  outline: none;
  transition: border-color 0.2s;
  font-weight: 600;
  border-bottom: 1px solid #484848;

  &::placeholder {
    color: #848484;
  }

  &:focus {
    border-color: #f3e9d7;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  min-height: 24px;
  border: none;
  background: transparent;
  color: #f3e9d7;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  outline: none;
  resize: none;
  overflow: hidden;
  transition: border-color 0.2s;
  border-bottom: 1px solid #484848;

  &::placeholder {
    color: #848484;
  }

  &:focus {
    border-color: #f3e9d7;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
`;

const DescriptionLabelContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const GenerateButton = styled.button`
  background: none;
  border: 1px solid #17c964;
  color: #17c964;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #17c964;
    color: #03041a;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RegenerateContainer = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: rgba(23, 201, 100, 0.1);
  border-radius: 4px;
`;

const RegenerateInput = styled.textarea`
  width: 100%;
  min-height: 60px;
  background: transparent;
  border: 1px solid #484848;
  border-radius: 4px;
  padding: 8px;
  color: #f3e9d7;
  font-size: 13px;
  resize: vertical;
  font-family: Inter;

  &::placeholder {
    color: #848484;
  }

  &:focus {
    outline: none;
    border-color: #17c964;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RegenerateActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  justify-content: flex-end;
`;

const RegenerateButton = styled.button`
  background: #17c964;
  color: #03041a;
  border: none;
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    filter: brightness(1.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelRegenerateButton = styled.button`
  background: transparent;
  color: #848484;
  border: 1px solid #484848;
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: #848484;
    color: #f3e9d7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyStateContainer = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const EmptyStateText = styled.p`
  color: #848484;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 24px;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const FormButton = styled.button<{ $outlined?: boolean }>`
  display: block;
  height: 48px;
  padding: 12px 24px;
  border-radius: 8px;
  background: ${props => (props.$outlined ? 'transparent' : '#17c964')};
  color: ${props => (props.$outlined ? '#17c964' : '#03041a')};
  border: ${props => (props.$outlined ? '1px solid #17c964' : 'none')};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.2s;
  opacity: ${props => (props.disabled ? 0.6 : 1)};

  &:disabled {
    color: #848484;
    border: 1px solid #848484;
    background: transparent;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    filter: brightness(1.2) saturate(1.2);
  }
`;

// Component definitions
interface GoalFormProps {
  goal: BrandingGoal;
  onSave: (goal: BrandingGoal) => void;
  onCancel: () => void;
  isSaving: boolean;
}

function GoalForm({ goal, onSave, onCancel, isSaving }: GoalFormProps) {
  const isEditMode = !!goal.id && !goal.id.includes('temp-');
  const [description, setDescription] = useState(goal.finalPrompt);
  const [label, setLabel] = useState(goal.label);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [showRegenerateInput, setShowRegenerateInput] = useState(false);
  const [regenerateInstructions, setRegenerateInstructions] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [editedTitle, setEditedTitle] = useState(goal.label);
  const [editedDescription, setEditedDescription] = useState(goal.finalPrompt);

  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const chatGPTServiceRef = useRef<ChatGPTService | null>(null);
  const descriptionTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Initialize ChatGPTService when component mounts
  useEffect(() => {
    if (videoCreator && datoClientStore?.aiPromptRepository) {
      chatGPTServiceRef.current = new ChatGPTService(
        videoCreator,
        datoClientStore.aiPromptRepository,
      );
    }
  }, [videoCreator, datoClientStore]);

  // Update edited values when goal changes (e.g., when entering edit mode)
  useEffect(() => {
    setEditedTitle(goal.label);
    setEditedDescription(goal.finalPrompt);
  }, [goal.id, goal.label, goal.finalPrompt]);

  // Auto-resize textarea to fit content
  const autoResizeTextarea = (textarea: HTMLTextAreaElement | null) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    autoResizeTextarea(descriptionTextareaRef.current);
  }, [editedDescription, isEditMode]);

  const handleEnhanceWithAI = async () => {
    // Need either title or description to enhance
    const inputText = description.trim() || label.trim();
    if (!inputText || !chatGPTServiceRef.current) return;

    setIsEnhancing(true);
    setError(null);

    try {
      const result = await chatGPTServiceRef.current.generateGoal({
        goalName: inputText,
        existingGoal: undefined, // Force title generation
      });

      if (typeof result === 'object' && result.title && result.description) {
        setLabel(result.title);
        onSave({
          ...goal,
          label: result.title,
          prePrompt: inputText, // Save original input
          finalPrompt: result.description, // Save AI-enhanced description
        });
      } else {
        const desc = typeof result === 'string' ? result : result.description;
        onSave({
          ...goal,
          label: label || 'New Goal',
          prePrompt: inputText,
          finalPrompt: desc,
        });
      }
    } catch (err) {
      console.error('Error enhancing goal with AI:', err);
      setError('Failed to enhance goal. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleRegenerateDescription = async () => {
    if (!regenerateInstructions.trim() || !chatGPTServiceRef.current) return;

    setIsEnhancing(true);
    setError(null);

    try {
      const result = await chatGPTServiceRef.current.generateGoal({
        goalName: label,
        customInstructions: regenerateInstructions,
        existingGoal: goal.finalPrompt, // Pass existing for regeneration (no title)
      });

      const newDescription =
        typeof result === 'string' ? result : result.description;

      onSave({
        ...goal,
        finalPrompt: newDescription, // Only update finalPrompt, keep title and prePrompt
      });

      setShowRegenerateInput(false);
      setRegenerateInstructions('');
    } catch (err) {
      console.error('Error regenerating goal description:', err);
      setError('Failed to regenerate description. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleSaveGoal = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate at least one field is filled
    if (!label.trim() && !description.trim()) {
      setError('Please enter either a title or a description');
      return;
    }

    // In edit mode, use edited values
    if (isEditMode) {
      onSave({
        ...goal,
        label: editedTitle.trim() || goal.label,
        finalPrompt: editedDescription.trim() || goal.finalPrompt,
        prePrompt: goal.prePrompt, // Keep original prePrompt
      });
      return;
    }

    // For new goals, save with whatever is provided
    onSave({
      ...goal,
      label: label.trim() || 'New Goal',
      prePrompt: description.trim() || label.trim(), // Use title as prePrompt if no description
      finalPrompt: description.trim() || label.trim(), // Same as prePrompt when not enhanced
    });
  };

  return (
    <FormContainer onSubmit={handleSaveGoal}>
      <FormFields>
        {/* Edit mode - show input fields directly */}
        {isEditMode && (
          <>
            <div>
              <div
                style={{
                  color: '#848484',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                }}
              >
                Goal Title
              </div>
              <FormInput
                type="text"
                value={editedTitle}
                onChange={e => setEditedTitle(e.target.value)}
                placeholder="Enter a title for this goal"
                disabled={isSaving || isEnhancing}
              />
            </div>

            <div>
              <DescriptionLabelContainer>
                <span
                  style={{
                    color: '#848484',
                    fontSize: '12px',
                    textTransform: 'uppercase',
                  }}
                >
                  Description
                </span>
                {!showRegenerateInput && (
                  <GenerateButton
                    type="button"
                    onClick={() => setShowRegenerateInput(true)}
                    disabled={isEnhancing}
                  >
                    Regenerate
                  </GenerateButton>
                )}
              </DescriptionLabelContainer>
              <FormTextarea
                ref={descriptionTextareaRef}
                value={editedDescription}
                onChange={e => {
                  setEditedDescription(e.target.value);
                  autoResizeTextarea(e.target);
                }}
                rows={1}
                placeholder="Describe your goal"
                disabled={isSaving || isEnhancing || showRegenerateInput}
              />

              {showRegenerateInput && (
                <RegenerateContainer>
                  <RegenerateInput
                    value={regenerateInstructions}
                    onChange={e => setRegenerateInstructions(e.target.value)}
                    placeholder="How would you like to improve the goal description? (e.g., 'Make it more specific', 'Focus on ROI', 'Add measurable outcomes')"
                    disabled={isEnhancing}
                  />
                  <RegenerateActions>
                    <RegenerateButton
                      type="button"
                      onClick={handleRegenerateDescription}
                      disabled={isEnhancing || !regenerateInstructions.trim()}
                    >
                      {isEnhancing ? 'Regenerating...' : 'Regenerate'}
                    </RegenerateButton>
                    <CancelRegenerateButton
                      type="button"
                      onClick={() => {
                        setShowRegenerateInput(false);
                        setRegenerateInstructions('');
                      }}
                      disabled={isEnhancing}
                    >
                      Cancel
                    </CancelRegenerateButton>
                  </RegenerateActions>
                </RegenerateContainer>
              )}

              {goal.prePrompt && goal.prePrompt !== goal.finalPrompt && (
                <div style={{ marginTop: '16px' }}>
                  <span
                    style={{
                      color: '#848484',
                      fontSize: '12px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Original Description
                  </span>
                  <p
                    style={{
                      color: '#848484',
                      fontSize: '13px',
                      lineHeight: '1.5',
                      marginTop: '4px',
                    }}
                  >
                    {goal.prePrompt}
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* New goal creation mode */}
        {!isEditMode && (
          <>
            <FormInput
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="Enter a title for this goal (optional)"
              disabled={isSaving || isEnhancing}
              autoFocus
            />

            <FormTextarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={4}
              placeholder="Describe your goal. For example: 'We want to expand our digital presence and attract younger investors who value sustainable investing and modern technology solutions.'"
              disabled={isSaving || isEnhancing}
            />
          </>
        )}

        {error && (
          <div style={{ color: '#ff4545', fontSize: '12px', marginTop: '8px' }}>
            {error}
          </div>
        )}
      </FormFields>

      <FormActions>
        <FormButton
          type="button"
          onClick={onCancel}
          disabled={isSaving || isEnhancing}
          $outlined
        >
          Cancel
        </FormButton>

        {!isEditMode && (
          <>
            <FormButton
              type="submit"
              disabled={
                isSaving ||
                isEnhancing ||
                (!label.trim() && !description.trim())
              }
            >
              {isSaving ? 'Saving...' : 'Save Goal'}
            </FormButton>

            <FormButton
              type="button"
              onClick={handleEnhanceWithAI}
              disabled={
                isSaving ||
                isEnhancing ||
                (!label.trim() && !description.trim())
              }
            >
              {isEnhancing ? 'Enhancing...' : 'Enhance with AI'}
            </FormButton>
          </>
        )}

        {isEditMode && (
          <FormButton type="submit" disabled={isSaving || isEnhancing}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </FormButton>
        )}
      </FormActions>
    </FormContainer>
  );
}

interface GoalItemProps {
  goal: BrandingGoal;
  isEditing: boolean;
  onEdit: () => void;
  onSave: (goal: BrandingGoal) => void;
  onDelete: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

function GoalItem({
  goal,
  isEditing,
  onEdit,
  onSave,
  onDelete,
  onCancel,
  isSaving,
}: GoalItemProps) {
  if (isEditing) {
    return (
      <GoalForm
        goal={goal}
        onSave={onSave}
        onCancel={onCancel}
        isSaving={isSaving}
      />
    );
  }

  return (
    <GoalItemContainer>
      <GoalContent>
        <GoalLabel>{goal.label}</GoalLabel>
        <GoalDescription>{goal.finalPrompt}</GoalDescription>
      </GoalContent>
      <GoalActions>
        <IconButton onClick={onEdit} disabled={isSaving} isEdit>
          <PencilIcon width="22" height="22" strokeColor="#848484" />
        </IconButton>
        <IconButton onClick={onDelete} disabled={isSaving} isDelete>
          <DeleteIcon width="22" height="22" strokeColor="#848484" />
        </IconButton>
      </GoalActions>
    </GoalItemContainer>
  );
}

const BrandKitGoals = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();

  const branding = videoCreator.organization?.branding;
  const [goals, setGoals] = useState<BrandingGoal[]>(branding?.goals || []);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [editingGoal, setEditingGoal] = useState<BrandingGoal | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isGeneratingMultiple, setIsGeneratingMultiple] = useState(false);
  const [isGeneratingNew, setIsGeneratingNew] = useState(false);
  const chatGPTServiceRef = useRef<ChatGPTService | null>(null);

  // Initialize ChatGPTService when component mounts
  useEffect(() => {
    if (videoCreator && datoClientStore?.aiPromptRepository) {
      chatGPTServiceRef.current = new ChatGPTService(
        videoCreator,
        datoClientStore.aiPromptRepository,
      );
    }
  }, [videoCreator, datoClientStore]);

  useEffect(() => {
    if (branding?.goals) setGoals(branding.goals);
  }, [branding?.goals]);

  const handleCreateGoal = async (goalData: Omit<BrandingGoal, 'id'>) => {
    const itemType = await datoClientStore.datoClient.itemTypes.find('goal');
    const createdGoal = await datoClientStore.datoClient.items.create({
      item_type: { type: 'item_type', id: itemType.id },
      label: goalData.label,
      pre_prompt: goalData.prePrompt,
      final_prompt: goalData.finalPrompt,
    });
    await datoClientStore.datoClient.items.publish(createdGoal.id);
    return createdGoal;
  };

  const handleUpdateGoal = async (goal: BrandingGoal) => {
    await datoClientStore.datoClient.items.update(goal.id, {
      label: goal.label,
      pre_prompt: goal.prePrompt,
      final_prompt: goal.finalPrompt,
    });
    await datoClientStore.datoClient.items.publish(goal.id);
  };

  const handleDeleteGoal = async (goalId: string) => {
    await datoClientStore.datoClient.items.destroy(goalId);
  };

  const updateBrandingWithGoals = async (updatedGoals: BrandingGoal[]) => {
    const { organization } = videoCreator;
    if (!organization?.branding) return;

    organization.branding = {
      ...organization.branding,
      goals: updatedGoals,
    };
    await datoClientStore.albumRepository?.update(organization);
  };

  const handleAddGoal = () => {
    const newGoal: BrandingGoal = {
      id: `temp-${Date.now()}`,
      label: '',
      prePrompt: '',
      finalPrompt: '',
    };
    setEditingGoal(newGoal);
    setIsAddingNew(true);
  };

  // Remove auto-open form to allow showing Generate 4 button
  // useEffect(() => {
  //   if (goals.length === 0 && !editingGoal && !isAddingNew) {
  //     handleAddGoal();
  //   }
  // }, [goals.length, editingGoal, isAddingNew]);

  const handleSaveGoal = async (goal: BrandingGoal) => {
    setIsSaving(true);
    setIsSaved(false);

    try {
      if (isAddingNew) {
        // Store original state for potential reversion
        const originalGoals = goals;

        // Immediately update UI state for responsive feedback
        const tempGoal: BrandingGoal = {
          ...goal,
          id: goal.id, // Temp ID
        };
        const optimisticGoals = [...goals, tempGoal];

        // Update UI immediately
        setEditingGoal(null);
        setIsAddingNew(false);
        setGoals(optimisticGoals);

        // Create new goal record in background
        const createdGoal = await handleCreateGoal({
          label: goal.label,
          prePrompt: goal.prePrompt,
          finalPrompt: goal.finalPrompt,
        });

        // Update with real ID and sync with branding
        const newGoal: BrandingGoal = {
          id: createdGoal.id,
          label: goal.label,
          prePrompt: goal.prePrompt,
          finalPrompt: goal.finalPrompt,
        };
        const updatedGoals = [...originalGoals, newGoal];

        // Update branding with new goal reference
        await updateBrandingWithGoals(updatedGoals);

        // Update with final data
        setGoals(updatedGoals);
      } else {
        // For edits, update UI immediately
        const updatedGoals = goals.map(g => (g.id === goal.id ? goal : g));
        setEditingGoal(null);
        setGoals(updatedGoals);

        // Update existing goal record in background
        await handleUpdateGoal(goal);

        // Update branding reference when editing
        await updateBrandingWithGoals(updatedGoals);
      }

      setIsSaving(false);
      setIsSaved(true);
    } catch (error) {
      console.error('Error saving goal:', error);
      setIsSaving(false);
      alert('Failed to save goal. Please try again.');

      // Revert optimistic update on error
      if (isAddingNew) {
        const originalGoals = goals; // Restore to pre-optimistic state
        setGoals(originalGoals);
        setEditingGoal(goal); // Show form again
        setIsAddingNew(true);
      }
    }
  };

  const handleRemoveGoal = async (goalId: string) => {
    setIsSaving(true);
    setIsSaved(false);

    // Store original state for potential restoration
    const originalGoals = goals;
    const goalToDelete = goals.find(g => g.id === goalId);
    if (!goalToDelete) return;

    // Immediately remove from UI for responsive feedback
    const optimisticGoals = goals.filter(g => g.id !== goalId);
    setGoals(optimisticGoals);

    try {
      // Delete the goal record in background
      await handleDeleteGoal(goalId);

      // Update branding to remove goal reference
      await updateBrandingWithGoals(optimisticGoals);

      setIsSaving(false);
      setIsSaved(true);
    } catch (error) {
      console.error('Error deleting goal:', error);
      setIsSaving(false);
      alert('Failed to delete goal. Please try again.');

      // Restore to original state before optimistic update
      setGoals(originalGoals);
    }
  };

  const handleCancelEdit = () => {
    setEditingGoal(null);
    setIsAddingNew(false);
  };

  const handleGenerateMultipleGoals = async () => {
    if (!chatGPTServiceRef.current) return;

    setIsGeneratingMultiple(true);
    setIsSaved(false);

    try {
      const generatedGoals =
        await chatGPTServiceRef.current.generateMultipleGoals(4);

      // Create all goals in parallel
      const createdGoals = await Promise.all(
        generatedGoals.map(async goalData => {
          const created = await handleCreateGoal({
            label: goalData.title,
            prePrompt: goalData.title, // Store the title as the original input
            finalPrompt: goalData.description,
          });

          return {
            id: created.id,
            label: goalData.title,
            prePrompt: goalData.title,
            finalPrompt: goalData.description,
          } as BrandingGoal;
        }),
      );

      // Update state and branding
      const updatedGoals = [...goals, ...createdGoals];
      await updateBrandingWithGoals(updatedGoals);
      setGoals(updatedGoals);
      setIsSaved(true);
    } catch (error) {
      console.error('Error generating multiple goals:', error);
      alert('Failed to generate goals. Please try again.');
    } finally {
      setIsGeneratingMultiple(false);
    }
  };

  const handleGenerateNewGoal = async () => {
    if (!chatGPTServiceRef.current) return;

    setIsGeneratingNew(true);
    setIsSaved(false);

    try {
      // Get existing goal context
      const existingContext =
        goals.length > 0
          ? `\n\nExisting goals to differentiate from:\n${goals
              .map(g => `- ${g.label}: ${g.finalPrompt}`)
              .join('\n')}`
          : '';

      const result = await chatGPTServiceRef.current.generateGoal({
        customInstructions: `Generate a new goal that is distinct from the existing ones.${existingContext}`,
      });

      if (typeof result === 'object' && result.title && result.description) {
        // Create the new goal
        const created = await handleCreateGoal({
          label: result.title,
          prePrompt: result.title,
          finalPrompt: result.description,
        });

        const newGoal: BrandingGoal = {
          id: created.id,
          label: result.title,
          prePrompt: result.title,
          finalPrompt: result.description,
        };

        // Update state and branding
        const updatedGoals = [...goals, newGoal];
        await updateBrandingWithGoals(updatedGoals);
        setGoals(updatedGoals);
        setIsSaved(true);
      }
    } catch (error) {
      console.error('Error generating new goal:', error);
      alert('Failed to generate goal. Please try again.');
    } finally {
      setIsGeneratingNew(false);
    }
  };

  return (
    <Accordion
      id="brand-goals"
      title="Goals"
      isSaving={isSaving}
      isSaved={isSaved}
    >
      <GoalsContainer>
        {goals.length === 0 && !editingGoal && (
          <EmptyStateContainer>
            <EmptyStateText>
              No goals defined yet. Generate AI-powered goals or add your own.
            </EmptyStateText>
          </EmptyStateContainer>
        )}

        {goals.map(goal => (
          <GoalItem
            key={goal.id}
            goal={goal}
            isEditing={editingGoal?.id === goal.id}
            onEdit={() => setEditingGoal(goal)}
            onSave={handleSaveGoal}
            onDelete={() => handleRemoveGoal(goal.id)}
            onCancel={handleCancelEdit}
            isSaving={isSaving}
          />
        ))}

        {!!branding && editingGoal && isAddingNew && (
          <GoalForm
            goal={editingGoal}
            onSave={handleSaveGoal}
            onCancel={handleCancelEdit}
            isSaving={isSaving}
          />
        )}

        {!editingGoal && (
          <ButtonContainer>
            {goals.length === 0 && (
              <FormButton
                type="button"
                onClick={handleGenerateMultipleGoals}
                disabled={isSaving || isGeneratingMultiple}
              >
                {isGeneratingMultiple ? 'Generating...' : 'Generate 4 Goals'}
              </FormButton>
            )}
            {goals.length > 0 && (
              <FormButton
                type="button"
                onClick={handleGenerateNewGoal}
                disabled={isSaving || isGeneratingNew}
                $outlined
              >
                {isGeneratingNew ? 'Generating...' : 'Generate New'}
              </FormButton>
            )}
            <AddButton
              onClick={handleAddGoal}
              disabled={isSaving || isGeneratingMultiple || isGeneratingNew}
            >
              Add New
            </AddButton>
          </ButtonContainer>
        )}
      </GoalsContainer>
    </Accordion>
  );
});

export default BrandKitGoals;
